import { useState } from 'react'
import { Link } from 'react-router-dom'
import PostMioLogo from '../components/PostMioLogo'

const STRIPE_TRIAL = null // payments disabled

// ── i18n (UI only — M-LATS output language is controlled separately) ──────
const DEMO_UI = {
  IT: {
    tag: 'Demo gratuita',
    h1: 'Prova PostMio™ — Nessuna carta richiesta',
    sub: '2 generazioni gratuite · Vedi con i tuoi occhi',
    tabs: { posts: 'Crea Post', review: 'Rispondi a Recensione' },
    labels: { business: 'Attività:', tone: 'Tono:', lang: 'Lingua:' },
    businessOpts: { gastronomy: '🍕 Ristorazione', beauty: '💇 Beauty & Wellness', design: '🎨 Grafica & Creative', coaching: '🎤 Coaching & Consulting', hospitality: '🏨 Ospitalità & Turismo', retail: '🛍️ Retail & Shop' },
    toneOpts: { professional: '💼 Professionale', friendly: '😊 Amichevole', fun: '🎉 Divertente' },
    placeholder: { posts: 'Raccontaci la tua giornata o un aggiornamento...', review: 'Incolla qui la recensione del cliente...' },
    minChars: n => `Minimo 20 caratteri (${n}/20)`,
    generating: 'Generazione in corso...',
    generate: 'Genera →',
    limitTitle: 'Hai raggiunto il limite di 2 generazioni gratuite per oggi.',
    limitSub: 'Torna domani, oppure sblocca tutto con la prova gratuita.',
    limitCta: 'Inizia la prova gratuita →',
    counter: n => `${n} di 2 generazioni utilizzate`,
    postsLabel: 'I tuoi post',
    reviewLabel: 'Risposta generata',
    reviewCardLabel: 'Risposta alla recensione',
    ctaH2: 'Pronto a pubblicare ogni giorno?',
    ctaSub: 'Prova PostMio gratis per 7 giorni. Nessun impegno.',
    ctaBtn: 'Inizia la prova gratuita — 7 giorni gratis →',
    ctaFine: 'Poi €9.99/mese. Cancella quando vuoi.',
    copy: 'Copia',
    copied: 'Copiato ✓',
    error: 'Errore durante la generazione. Riprova.',
    networkError: 'Errore di rete. Controlla la connessione e riprova.',
  },
  EN: {
    tag: 'Free demo',
    h1: 'Try PostMio™ — No card required',
    sub: '2 free generations · See for yourself',
    tabs: { posts: 'Create Posts', review: 'Reply to Review' },
    labels: { business: 'Business:', tone: 'Tone:', lang: 'Language:' },
    businessOpts: { gastronomy: '🍕 Food & Drink', beauty: '💇 Beauty & Wellness', design: '🎨 Design & Creative', coaching: '🎤 Coaching & Consulting', hospitality: '🏨 Hospitality & Tourism', retail: '🛍️ Retail & Shop' },
    toneOpts: { professional: '💼 Professional', friendly: '😊 Friendly', fun: '🎉 Fun' },
    placeholder: { posts: 'Tell us about your day or an update...', review: 'Paste the customer review here...' },
    minChars: n => `Minimum 20 characters (${n}/20)`,
    generating: 'Generating...',
    generate: 'Generate →',
    limitTitle: "You've reached the limit of 2 free generations for today.",
    limitSub: 'Come back tomorrow, or unlock everything with the free trial.',
    limitCta: 'Start free trial →',
    counter: n => `${n} of 2 generations used`,
    postsLabel: 'Your posts',
    reviewLabel: 'Generated reply',
    reviewCardLabel: 'Review reply',
    ctaH2: 'Ready to post every day?',
    ctaSub: 'Try PostMio free for 7 days. No commitment.',
    ctaBtn: 'Start free trial — 7 days free →',
    ctaFine: 'Then €9.99/month. Cancel anytime.',
    copy: 'Copy',
    copied: 'Copied ✓',
    error: 'Error generating. Please try again.',
    networkError: 'Network error. Check your connection and try again.',
  },
  PL: {
    tag: 'Darmowe demo',
    h1: 'Wypróbuj PostMio™ — Bez karty',
    sub: '2 darmowe generacje · Przekonaj się sam',
    tabs: { posts: 'Twórz posty', review: 'Odpowiedz na opinię' },
    labels: { business: 'Działalność:', tone: 'Ton:', lang: 'Język:' },
    businessOpts: { gastronomy: '🍕 Gastronomia', beauty: '💇 Beauty & Wellness', design: '🎨 Grafika & Kreacja', coaching: '🎤 Coaching & Consulting', hospitality: '🏨 Hotelarstwo & Turystyka', retail: '🛍️ Retail & Sklep' },
    toneOpts: { professional: '💼 Profesjonalny', friendly: '😊 Przyjazny', fun: '🎉 Zabawny' },
    placeholder: { posts: 'Opisz swój dzień lub aktualizację...', review: 'Wklej tutaj opinię klienta...' },
    minChars: n => `Minimum 20 znaków (${n}/20)`,
    generating: 'Generowanie...',
    generate: 'Generuj →',
    limitTitle: 'Osiągnąłeś limit 2 darmowych generacji na dziś.',
    limitSub: 'Wróć jutro lub odblokuj wszystko bezpłatnym okresem próbnym.',
    limitCta: 'Rozpocznij bezpłatny okres próbny →',
    counter: n => `${n} z 2 generacji użyte`,
    postsLabel: 'Twoje posty',
    reviewLabel: 'Wygenerowana odpowiedź',
    reviewCardLabel: 'Odpowiedź na opinię',
    ctaH2: 'Gotowy do codziennego postowania?',
    ctaSub: 'Wypróbuj PostMio za darmo przez 7 dni. Bez zobowiązań.',
    ctaBtn: 'Rozpocznij bezpłatny okres próbny — 7 dni za darmo →',
    ctaFine: 'Potem €9,99/mies. Anuluj w każdej chwili.',
    copy: 'Kopiuj',
    copied: 'Skopiowano ✓',
    error: 'Błąd generowania. Spróbuj ponownie.',
    networkError: 'Błąd sieci. Sprawdź połączenie i spróbuj ponownie.',
  },
  ES: {
    tag: 'Demo gratuita',
    h1: 'Prueba PostMio™ — Sin tarjeta',
    sub: '2 generaciones gratuitas · Compruébalo tú mismo',
    tabs: { posts: 'Crear publicaciones', review: 'Responder reseña' },
    labels: { business: 'Negocio:', tone: 'Tono:', lang: 'Idioma:' },
    businessOpts: { gastronomy: '🍕 Gastronomía', beauty: '💇 Beauty & Wellness', design: '🎨 Diseño & Creativo', coaching: '🎤 Coaching & Consultoría', hospitality: '🏨 Hostelería & Turismo', retail: '🛍️ Retail & Tienda' },
    toneOpts: { professional: '💼 Profesional', friendly: '😊 Amistoso', fun: '🎉 Divertido' },
    placeholder: { posts: 'Cuéntanos tu día o una actualización...', review: 'Pega aquí la reseña del cliente...' },
    minChars: n => `Mínimo 20 caracteres (${n}/20)`,
    generating: 'Generando...',
    generate: 'Generar →',
    limitTitle: 'Has alcanzado el límite de 2 generaciones gratuitas por hoy.',
    limitSub: 'Vuelve mañana o desbloquea todo con la prueba gratuita.',
    limitCta: 'Iniciar prueba gratuita →',
    counter: n => `${n} de 2 generaciones utilizadas`,
    postsLabel: 'Tus publicaciones',
    reviewLabel: 'Respuesta generada',
    reviewCardLabel: 'Respuesta a la reseña',
    ctaH2: '¿Listo para publicar cada día?',
    ctaSub: 'Prueba PostMio gratis durante 7 días. Sin compromiso.',
    ctaBtn: 'Iniciar prueba gratuita — 7 días gratis →',
    ctaFine: 'Después €9,99/mes. Cancela cuando quieras.',
    copy: 'Copiar',
    copied: 'Copiado ✓',
    error: 'Error al generar. Inténtalo de nuevo.',
    networkError: 'Error de red. Comprueba tu conexión e inténtalo de nuevo.',
  },
  DE: {
    tag: 'Kostenlose Demo',
    h1: 'PostMio™ ausprobieren — Keine Karte nötig',
    sub: '2 kostenlose Generierungen · Überzeug dich selbst',
    tabs: { posts: 'Beiträge erstellen', review: 'Bewertung beantworten' },
    labels: { business: 'Branche:', tone: 'Ton:', lang: 'Sprache:' },
    businessOpts: { gastronomy: '🍕 Gastronomie', beauty: '💇 Beauty & Wellness', design: '🎨 Design & Kreativ', coaching: '🎤 Coaching & Beratung', hospitality: '🏨 Gastgewerbe & Tourismus', retail: '🛍️ Einzelhandel & Shop' },
    toneOpts: { professional: '💼 Professionell', friendly: '😊 Freundlich', fun: '🎉 Locker' },
    placeholder: { posts: 'Erzähl uns von deinem Tag oder einem Update...', review: 'Füge hier die Kundenbewertung ein...' },
    minChars: n => `Mindestens 20 Zeichen (${n}/20)`,
    generating: 'Wird erstellt...',
    generate: 'Erstellen →',
    limitTitle: 'Du hast das Limit von 2 kostenlosen Generierungen für heute erreicht.',
    limitSub: 'Komm morgen wieder oder schalte alles mit der kostenlosen Testversion frei.',
    limitCta: 'Kostenlose Testversion starten →',
    counter: n => `${n} von 2 Generierungen genutzt`,
    postsLabel: 'Deine Beiträge',
    reviewLabel: 'Generierte Antwort',
    reviewCardLabel: 'Antwort auf Bewertung',
    ctaH2: 'Bereit, jeden Tag zu posten?',
    ctaSub: 'Teste PostMio 7 Tage kostenlos. Keine Verpflichtung.',
    ctaBtn: 'Kostenlose Testversion starten — 7 Tage gratis →',
    ctaFine: 'Danach €9,99/Monat. Jederzeit kündbar.',
    copy: 'Kopieren',
    copied: 'Kopiert ✓',
    error: 'Fehler beim Erstellen. Bitte versuche es erneut.',
    networkError: 'Netzwerkfehler. Überprüfe deine Verbindung und versuche es erneut.',
  },
  FR: {
    tag: 'Démo gratuite',
    h1: 'Essayez PostMio™ — Sans carte requise',
    sub: '2 générations gratuites · Voyez par vous-même',
    tabs: { posts: 'Créer des publications', review: 'Répondre à un avis' },
    labels: { business: 'Activité :', tone: 'Ton :', lang: 'Langue :' },
    businessOpts: { gastronomy: '🍕 Restauration', beauty: '💇 Beauté & Bien-être', design: '🎨 Design & Créatif', coaching: '🎤 Coaching & Conseil', hospitality: '🏨 Hôtellerie & Tourisme', retail: '🛍️ Commerce & Boutique' },
    toneOpts: { professional: '💼 Professionnel', friendly: '😊 Amical', fun: '🎉 Amusant' },
    placeholder: { posts: "Parlez-nous de votre journée ou d'une mise à jour...", review: "Collez ici l'avis du client..." },
    minChars: n => `Minimum 20 caractères (${n}/20)`,
    generating: 'Génération en cours...',
    generate: 'Générer →',
    limitTitle: "Vous avez atteint la limite de 2 générations gratuites pour aujourd'hui.",
    limitSub: "Revenez demain ou déverrouillez tout avec l'essai gratuit.",
    limitCta: "Commencer l'essai gratuit →",
    counter: n => `${n} sur 2 générations utilisées`,
    postsLabel: 'Vos publications',
    reviewLabel: 'Réponse générée',
    reviewCardLabel: "Réponse à l'avis",
    ctaH2: 'Prêt à publier chaque jour ?',
    ctaSub: 'Essayez PostMio gratuitement pendant 7 jours. Sans engagement.',
    ctaBtn: "Commencer l'essai gratuit — 7 jours offerts →",
    ctaFine: 'Puis €9,99/mois. Résiliez quand vous voulez.',
    copy: 'Copier',
    copied: 'Copié ✓',
    error: 'Erreur lors de la génération. Réessayez.',
    networkError: 'Erreur réseau. Vérifiez votre connexion et réessayez.',
  },
}

// Same detectLang as Landing.jsx
function detectLang() {
  const lang = (navigator.language || '').toLowerCase()
  if (lang.startsWith('pl')) return 'PL'
  if (lang.startsWith('en')) return 'EN'
  if (lang.startsWith('es')) return 'ES'
  if (lang.startsWith('de')) return 'DE'
  if (lang.startsWith('fr')) return 'FR'
  return 'IT'
}

function renderTM(s) {
  if (typeof s !== 'string' || !s.includes('™')) return s
  const i = s.indexOf('™')
  return <>{s.slice(0, i)}<sup style={{ fontSize: '0.5em', verticalAlign: 'super', lineHeight: 0 }}>™</sup>{s.slice(i + 1)}</>
}

// Exact same SVG icons as Dashboard (className="w-5 h-5")
const PLATFORM_ICONS = {
  instagram: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <circle cx="12" cy="12" r="4"/>
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
    </svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
    </svg>
  ),
}

// Exact same CopyButton as Dashboard
function CopyButton({ text, t }) {
  const [copied, setCopied] = useState(false)
  async function handleCopy() {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button
      onClick={handleCopy}
      className={`text-xs font-semibold px-4 py-2 rounded-lg transition-all tracking-wider ${
        copied
          ? 'bg-[#C9922A] text-[#0A0A0A]'
          : 'bg-[#2C2C2C] text-[#A0A0A0] hover:bg-[#C9922A]/20 hover:text-[#C9922A]'
      }`}
    >
      {copied ? t.copied : t.copy}
    </button>
  )
}

// Exact same PostCard as Dashboard
function PostCard({ platform, icon, content, t }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#2C2C2C] rounded-2xl p-6 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#C9922A]">
          {icon}
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#A0A0A0]">{platform}</span>
        </div>
        <CopyButton text={content} t={t} />
      </div>
      <p className="text-[#F0EDE8] text-sm leading-snug sm:leading-relaxed whitespace-pre-wrap">{content}</p>
    </div>
  )
}

// Shared select chevron — same as Dashboard
function Chevron() {
  return (
    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  )
}

export default function Demo() {
  const t = DEMO_UI[detectLang()]

  const [activeTab, setActiveTab]             = useState('posts')
  const [businessType, setBusinessType]       = useState('gastronomy')
  const [tone, setTone]                       = useState('professional')
  const [lang, setLang]                       = useState('IT')

  const [postText, setPostText]               = useState('')
  const [reviewText, setReviewText]           = useState('')

  const [loading, setLoading]                 = useState(false)
  const [posts, setPosts]                     = useState(null)
  const [reviewResult, setReviewResult]       = useState(null)
  const [generationsUsed, setGenerationsUsed] = useState(0)
  const [limitReached, setLimitReached]       = useState(false)
  const [error, setError]                     = useState(null)

  const inputText   = activeTab === 'posts' ? postText : reviewText
  const canGenerate = inputText.trim().length >= 20 && !loading && !limitReached

  async function handleGenerate() {
    setLoading(true)
    setError(null)
    setPosts(null)
    setReviewResult(null)

    const body = activeTab === 'posts'
      ? { mode: 'posts', text: postText, businessType, tone, language: lang }
      : { mode: 'review', reviewText, businessType, tone, language: lang }

    try {
      const res = await fetch('/api/demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      const data = await res.json()

      if (res.status === 429) {
        setLimitReached(true)
        setGenerationsUsed(2)
        return
      }

      if (!res.ok) {
        setError(data.error || t.error)
        return
      }

      if (activeTab === 'posts') {
        setPosts(data)
      } else {
        setReviewResult(data.review)
      }

      setGenerationsUsed(prev => Math.min(prev + 1, 2))
    } catch {
      setError(t.networkError)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0EDE8]" style={{ fontFamily: 'DM Sans, sans-serif' }}>

      {/* Nav — same height/structure as Dashboard, logo only */}
      <nav className="border-b border-[#2C2C2C] bg-[#0A0A0A] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-center">
          <Link to="/" className="inline-flex items-center">
            <PostMioLogo size="md" variant="dark" />
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-12">

        {/* Hero — same typography scale as Dashboard header */}
        <header className="text-center">
          <p className="text-[#7A7A7A] text-sm uppercase tracking-[0.2em] mb-2">{t.tag}</p>
          <h1 className="text-3xl font-bold text-[#F0EDE8]" style={{ fontFamily: 'Lora, serif' }}>
            {renderTM(t.h1)}
          </h1>
          <div className="w-10 h-0.5 bg-[#C9922A] mt-3 rounded-full mx-auto" />
          <p className="text-sm uppercase tracking-[0.2em] text-[#7A7A7A] mt-4">
            {t.sub}
          </p>
        </header>

        {/* Form card — same as Dashboard section card */}
        <section className="bg-[#141414] border border-[#2C2C2C] rounded-2xl overflow-hidden">

          {/* Tab switcher — gold underline on active, same tracking/uppercase as Dashboard nav labels */}
          <div className="flex border-b border-[#2C2C2C]">
            {[
              { id: 'posts',  label: t.tabs.posts },
              { id: 'review', label: t.tabs.review },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setPosts(null); setReviewResult(null); setError(null) }}
                className={`flex-1 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'text-[#C9922A] border-[#C9922A]'
                    : 'text-[#7A7A7A] border-transparent hover:text-[#A0A0A0]'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Form body */}
          <div className="p-8 space-y-6">

            {/* Selectors row — exact same pattern as Dashboard tone+businessType row */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">

              <div className="flex items-center gap-3">
                <label className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A] shrink-0">
                  {t.labels.business}
                </label>
                <div className="relative">
                  <select
                    value={businessType}
                    onChange={e => setBusinessType(e.target.value)}
                    className="appearance-none bg-[#0A0A0A] border border-[#2C2C2C] hover:border-[#C9922A]/50 focus:border-[#C9922A] text-[#F0EDE8] text-sm rounded-xl pl-4 pr-10 py-2.5 outline-none transition-all cursor-pointer w-[200px] max-w-full"
                  >
                    <option value="gastronomy"  className="bg-[#141414] text-[#F0EDE8]">{t.businessOpts.gastronomy}</option>
                    <option value="beauty"      className="bg-[#141414] text-[#F0EDE8]">{t.businessOpts.beauty}</option>
                    <option value="design"      className="bg-[#141414] text-[#F0EDE8]">{t.businessOpts.design}</option>
                    <option value="coaching"    className="bg-[#141414] text-[#F0EDE8]">{t.businessOpts.coaching}</option>
                    <option value="hospitality" className="bg-[#141414] text-[#F0EDE8]">{t.businessOpts.hospitality}</option>
                    <option value="retail"      className="bg-[#141414] text-[#F0EDE8]">{t.businessOpts.retail}</option>
                  </select>
                  <Chevron />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A] shrink-0">
                  {t.labels.tone}
                </label>
                <div className="relative">
                  <select
                    value={tone}
                    onChange={e => setTone(e.target.value)}
                    className="appearance-none bg-[#0A0A0A] border border-[#2C2C2C] hover:border-[#C9922A]/50 focus:border-[#C9922A] text-[#F0EDE8] text-sm rounded-xl pl-4 pr-10 py-2.5 outline-none transition-all cursor-pointer w-[180px] max-w-full"
                  >
                    <option value="professional" className="bg-[#141414] text-[#F0EDE8]">{t.toneOpts.professional}</option>
                    <option value="friendly"     className="bg-[#141414] text-[#F0EDE8]">{t.toneOpts.friendly}</option>
                    <option value="fun"          className="bg-[#141414] text-[#F0EDE8]">{t.toneOpts.fun}</option>
                  </select>
                  <Chevron />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A] shrink-0">
                  {t.labels.lang}
                </label>
                <div className="relative">
                  <select
                    value={lang}
                    onChange={e => setLang(e.target.value)}
                    className="appearance-none bg-[#0A0A0A] border border-[#2C2C2C] hover:border-[#C9922A]/50 focus:border-[#C9922A] text-[#F0EDE8] text-sm rounded-xl pl-4 pr-10 py-2.5 outline-none transition-all cursor-pointer w-[130px] max-w-full"
                  >
                    <option value="IT" className="bg-[#141414] text-[#F0EDE8]">🇮🇹 IT</option>
                    <option value="EN" className="bg-[#141414] text-[#F0EDE8]">🇬🇧 EN</option>
                    <option value="PL" className="bg-[#141414] text-[#F0EDE8]">🇵🇱 PL</option>
                    <option value="ES" className="bg-[#141414] text-[#F0EDE8]">🇪🇸 ES</option>
                    <option value="DE" className="bg-[#141414] text-[#F0EDE8]">🇩🇪 DE</option>
                    <option value="FR" className="bg-[#141414] text-[#F0EDE8]">🇫🇷 FR</option>
                  </select>
                  <Chevron />
                </div>
              </div>

            </div>

            {/* Textarea — exact same class as Dashboard */}
            <div>
              <textarea
                className="w-full bg-[#0A0A0A] border border-[#2C2C2C] rounded-xl p-5 text-[#F0EDE8] text-base focus:border-[#C9922A] outline-none transition-all resize-none h-36 placeholder-[#606060]"
                placeholder={activeTab === 'posts' ? t.placeholder.posts : t.placeholder.review}
                value={activeTab === 'posts' ? postText : reviewText}
                onChange={e => activeTab === 'posts' ? setPostText(e.target.value) : setReviewText(e.target.value)}
              />
              {inputText.trim().length > 0 && inputText.trim().length < 20 && (
                <p className="text-xs text-[#7A7A7A] mt-2">
                  {t.minChars(inputText.trim().length)}
                </p>
              )}
            </div>

            {/* CTA area */}
            {limitReached ? (
              /* 429 — amber border variant of the standard card */
              <div className="bg-[#141414] border border-[#92400E]/60 rounded-2xl p-6 space-y-3 text-center">
                <p className="text-sm font-bold text-[#FCD34D]">
                  {t.limitTitle}
                </p>
                <p className="text-sm text-[#A0A0A0]">
                  {t.limitSub}
                </p>
                <a
                  href={STRIPE_TRIAL}
                  className="inline-block mt-1 bg-[#C9922A] font-bold py-3 px-8 rounded-xl text-sm uppercase tracking-[0.2em]"
                  style={{ color: '#000000', cursor: 'default', opacity: 0.6, pointerEvents: 'none' }}
                >
                  🚀 Coming Soon — Stay tuned for launch!
                </a>
              </div>
            ) : (
              <div className="space-y-3">
                {error && (
                  <p className="text-sm text-[#A02020]">{error}</p>
                )}
                {/* Generate button — exact same as Dashboard primary button */}
                <button
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                  className="w-full bg-[#C9922A] hover:bg-[#E5B050] disabled:opacity-40 disabled:cursor-not-allowed font-bold py-4 rounded-xl text-sm uppercase tracking-[0.2em] transition-all"
                  style={{ color: '#000000' }}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-3">
                      <span className="w-4 h-4 border-2 border-[#0A0A0A]/30 border-t-[#0A0A0A] rounded-full animate-spin inline-block" />
                      {t.generating}
                    </span>
                  ) : t.generate}
                </button>
              </div>
            )}

            {/* Generation counter — subtle gold label, same tracking pattern as Dashboard gold labels */}
            {generationsUsed > 0 && (
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C9922A] text-center">
                {t.counter(generationsUsed)}
              </p>
            )}

          </div>
        </section>

        {/* Output — posts */}
        {posts && (
          <section className="space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-[0.3em] text-[#7A7A7A]">{t.postsLabel}</h3>
            <PostCard platform="Instagram" icon={PLATFORM_ICONS.instagram} content={posts.instagram} t={t} />
            <PostCard platform="Facebook"  icon={PLATFORM_ICONS.facebook}  content={posts.facebook}  t={t} />
            <PostCard platform="WhatsApp"  icon={PLATFORM_ICONS.whatsapp}  content={posts.whatsapp}  t={t} />
          </section>
        )}

        {/* Output — review */}
        {reviewResult && (
          <section className="space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-[0.3em] text-[#7A7A7A]">{t.reviewLabel}</h3>
            {/* Same inner card as Dashboard review result */}
            <div className="bg-[#0A0A0A] border border-[#2C2C2C] rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#7A7A7A]">{t.reviewCardLabel}</span>
                <CopyButton text={reviewResult} t={t} />
              </div>
              <p className="text-[#F0EDE8] text-sm leading-relaxed whitespace-pre-wrap">{reviewResult}</p>
            </div>
          </section>
        )}

        {/* Bottom CTA — same section card style as Dashboard */}
        <section className="bg-[#141414] border border-[#2C2C2C] rounded-2xl p-8 text-center space-y-4">
          <h2 className="text-2xl font-bold text-[#C9922A]" style={{ fontFamily: 'Lora, serif' }}>
            {t.ctaH2}
          </h2>
          <p className="text-sm text-[#A0A0A0] leading-relaxed max-w-sm mx-auto">
            {t.ctaSub}
          </p>
          <a
            href={STRIPE_TRIAL}
            className="inline-block bg-[#C9922A] font-bold py-4 px-10 rounded-xl text-sm uppercase tracking-[0.2em]"
            style={{ color: '#000000', cursor: 'default', opacity: 0.6, pointerEvents: 'none' }}
          >
            🚀 Coming Soon — Stay tuned for launch!
          </a>
          <p className="text-xs text-[#606060]">{t.ctaFine}</p>
        </section>

      </main>
    </div>
  )
}
